import { NgModule } from "@angular/core";
import { NoPreloading, RouterModule, Routes } from "@angular/router";

import { FeatureFlagEnum, RouteEnum } from "./shared/enums";
import {
  AccountOwnerOrAdminOrContributorGuard,
  AdminSectionGuard,
  DefaultRouteGuard,
  FeatureFlagGuard,
  LoggedInGuard,
  NotLoggedInGuard,
  NotSystemAdminGuard,
  RegularUserGuard,
  SharedUserGuard,
  SystemAdminGuard,
} from "./shared/guards";

const routes: Routes = [
  {
    path: RouteEnum.LOCATIONS,
    loadChildren: () =>
      import("./components/locations/locations.module").then((m) => m.LocationsModule),
    canActivate: [LoggedInGuard],
  },
  {
    path: RouteEnum.ORGANISATIONS,
    loadChildren: () =>
      import("./components/organisations/organisations.module").then((m) => m.OrganisationsModule),
    canActivate: [LoggedInGuard],
  },
  {
    path: RouteEnum.ORGANISATIONS_ADD,
    loadComponent: () =>
      import("./components/organisations/ui").then((c) => c.AddOrganisationComponent),
    canActivate: [LoggedInGuard, RegularUserGuard, NotSystemAdminGuard],
  },
  {
    path: RouteEnum.CERTIFICATES,
    loadChildren: () =>
      import("./components/certificates/certificates.module").then((m) => m.CertificatesModule),
    canActivate: [LoggedInGuard],
  },
  {
    path: RouteEnum.DELIVERIES,
    loadChildren: () =>
      import("./components/deliveries/deliveries.module").then((m) => m.DeliveriesModule),
    canActivate: [LoggedInGuard],
  },
  {
    path: RouteEnum.ITEMS,
    loadChildren: () => import("./components/items/items.module").then((m) => m.ItemsModule),
    canActivate: [LoggedInGuard],
  },
  {
    path: RouteEnum.DOCUMENTS,
    loadChildren: () =>
      import("./components/documents/documents.module").then((m) => m.DocumentsModule),
    canActivate: [LoggedInGuard],
  },
  {
    path: RouteEnum.ADMIN_ORGANISATIONS,
    loadChildren: () => import("./components/admin/admin.module").then((m) => m.AdminModule),
    canActivate: [LoggedInGuard, SystemAdminGuard],
  },

  {
    path: RouteEnum.ADMIN_USERS,
    loadChildren: () =>
      import("./components/admin/users/admin-users.module").then((m) => m.AdminUsersModule),
    canActivate: [LoggedInGuard, SystemAdminGuard],
  },
  {
    path: RouteEnum.ADMIN_STANDARDS,
    loadChildren: () =>
      import("./components/settings/settings-standards/settings-standards.module").then(
        (m) => m.SettingsStandardsModule,
      ),
    canActivate: [LoggedInGuard, SystemAdminGuard],
  },
  {
    path: RouteEnum.ADMIN,
    loadChildren: () => import("./components/settings/admin.module").then((m) => m.AdminModule),
    canActivate: [LoggedInGuard, AdminSectionGuard],
  },
  {
    path: RouteEnum.USER_SETTINGS,
    loadChildren: () =>
      import("./components/user-settings/user-settings.module").then((m) => m.UserSettingsModule),
    canActivate: [LoggedInGuard, RegularUserGuard],
  },
  {
    path: RouteEnum.INVITATIONS,
    loadChildren: () =>
      import("./components/invitations/invitations.module").then((m) => m.InvitationsModule),
    canActivate: [LoggedInGuard, RegularUserGuard, NotSystemAdminGuard],
  },
  {
    path: RouteEnum.PRODUCTS,
    loadChildren: () =>
      import("./components/products/products.module").then((m) => m.ProductsModule),
    canActivate: [LoggedInGuard],
  },
  {
    path: RouteEnum.MATERIALS,
    loadChildren: () =>
      import("./components/materials/materials.module").then((m) => m.MaterialsModule),
    canActivate: [LoggedInGuard],
  },
  {
    path: RouteEnum.SUPPLY_CHAINS,
    loadChildren: () =>
      import("./components/supply-chains/supply-chains.module").then((m) => m.SupplyChainsModule),
    canActivate: [LoggedInGuard],
  },
  {
    path: RouteEnum.PROCESSES,
    loadChildren: () =>
      import("./components/processes/processes.module").then((m) => m.ProcessesModule),
    canActivate: [LoggedInGuard],
  },
  {
    path: RouteEnum.INBOX,
    loadChildren: () => import("./components/inbox/inbox.module").then((m) => m.InboxModule),
    canActivate: [LoggedInGuard, FeatureFlagGuard, AccountOwnerOrAdminOrContributorGuard],
    data: { featureFlag: FeatureFlagEnum.CROSS_ORGANISATION_SHARING },
  },
  {
    path: RouteEnum.DASHBOARD,
    loadChildren: () =>
      import("./components/dashboard/dashboard.module").then((m) => m.DashboardModule),
    canActivate: [LoggedInGuard],
  },
  {
    path: RouteEnum.OAUTH_CALLBACK,
    loadComponent: () =>
      import("./components/guest/oauth/ui").then((c) => c.OAuthCallbackComponent),
    canActivate: [NotLoggedInGuard],
  },
  {
    path: RouteEnum.LOGIN,
    loadChildren: () => import("./components/guest/login/login.module").then((m) => m.LoginModule),
    canActivate: [NotLoggedInGuard],
  },
  {
    path: RouteEnum.LOGOUT,
    loadChildren: () =>
      import("./components/guest/logout/logout.module").then((m) => m.LogoutModule),
  },
  {
    path: RouteEnum.SIGN_UP,
    loadChildren: () =>
      import("./components/guest/sign-up/sign-up.module").then((m) => m.SignUpModule),
    canActivate: [NotLoggedInGuard],
  },
  {
    path: RouteEnum.VERIFY,
    loadChildren: () =>
      import("./components/guest/verify/verify.module").then((m) => m.VerifyModule),
    canActivate: [NotLoggedInGuard],
  },
  {
    path: RouteEnum.FORGOT_PASSWORD,
    loadChildren: () =>
      import("./components/guest/forgot-password/forgot-password.module").then(
        (m) => m.ForgotPasswordModule,
      ),
    canActivate: [NotLoggedInGuard],
  },
  {
    path: RouteEnum.RESET_PASSWORD,
    loadChildren: () =>
      import("./components/guest/reset-password/reset-password.module").then(
        (m) => m.ResetPasswordModule,
      ),
    canActivate: [NotLoggedInGuard],
  },
  {
    path: RouteEnum.SHARING,
    loadChildren: () =>
      import("./components/guest/sharing/sharing.module").then((m) => m.SharingModule),
  },
  {
    path: RouteEnum.REPORTS,
    loadChildren: () => import("./components/reports/reports.module").then((m) => m.ReportsModule),
    canActivate: [LoggedInGuard],
  },
  {
    path: `${RouteEnum.OVERLAY_RISK_ASSESSMENT_REPORT_PRINT_VIEW}/:id`,
    loadChildren: () =>
      import("./components/shared/risk-assessment-reports/risk-assessment-report.module").then(
        (m) => m.RiskAssessmentReportModule,
      ),
    canActivate: [LoggedInGuard, RegularUserGuard],
  },
  {
    path: RouteEnum.SHARED_LINK,
    loadChildren: () =>
      import("./components/shared-link/shared-link.module").then((m) => m.SharedLinkModule),
    canActivate: [LoggedInGuard, SharedUserGuard],
  },
  {
    path: RouteEnum.PURCHASE_ORDERS,
    loadChildren: () =>
      import("./components/purchase-orders/purchase-orders.module").then(
        (m) => m.PurchaseOrdersModule,
      ),
    canActivate: [LoggedInGuard, FeatureFlagGuard, RegularUserGuard],
    data: { featureFlag: FeatureFlagEnum.PURCHASE_ORDERS },
  },
  {
    path: "",
    pathMatch: "full",
    canActivate: [DefaultRouteGuard],
    children: [],
  },
  {
    path: "**",
    canActivate: [DefaultRouteGuard],
    children: [],
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes, { preloadingStrategy: NoPreloading })],
  exports: [RouterModule],
})
export class AppRoutingModule {}
